import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Key,
  ArrowLeft,
  RefreshCw,
  Trash2,
  Copy,
  Check,
  AlertCircle,
  Activity,
  Clock,
  Shield,
  Save,
  X,
} from 'lucide-react';
import { apiKeysAPI } from '../../services/api';
import { useApiKeyPermissions } from '../../hooks/useApiKeyPermissions';


export default function ApiKeyDetailPage() {
  const PERMISSIONS = useApiKeyPermissions();
  const { id } = useParams();
  const navigate = useNavigate();

  const [apiKey, setApiKey] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);

  // New key display (after rotation)
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);

  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRotateModal, setShowRotateModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rotateLoading, setRotateLoading] = useState(false);

  const fetchApiKey = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiKeysAPI.get(id);
      setApiKey(response.data.apiKey);
      setEditForm(response.data.apiKey);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load API key');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const response = await apiKeysAPI.getAudit(id, { limit: 50 });
      setAuditLogs(response.data.auditLogs);
    } catch (err) {
      console.error('Failed to load audit logs', err);
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKey();
    fetchAuditLogs();
  }, [id]);

  const handleSave = async () => {
    setSaveLoading(true);
    try {
      await apiKeysAPI.update(id, {
        name: editForm.name,
        description: editForm.description,
        permissions: editForm.permissions,
        rateLimitPerMinute: editForm.rateLimitPerMinute,
        rateLimitPerHour: editForm.rateLimitPerHour,
        isActive: editForm.isActive,
      });
      setIsEditing(false);
      fetchApiKey();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await apiKeysAPI.delete(id);
      navigate('/admin/api-keys');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete API key');
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleRotate = async () => {
    setRotateLoading(true);
    try {
      const response = await apiKeysAPI.rotate(id);
      setNewKey(response.data.apiKey);
      setShowRotateModal(false);
      fetchApiKey();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rotate API key');
    } finally {
      setRotateLoading(false);
    }
  };

  const handleCopyKey = async () => {
    if (newKey?.key) {
      await navigator.clipboard.writeText(newKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const togglePermission = (permission) => {
    setEditForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300';
    if (status >= 400 && status < 500) return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300';
    if (status >= 500) return 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300';
    return 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && !apiKey) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <AlertCircle className="h-8 w-8 text-red-500 dark:text-red-400 mx-auto" />
        <p className="text-red-700 dark:text-red-300 mt-2">{error}</p>
        <button
          onClick={() => navigate('/admin/api-keys')}
          className="mt-4 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
        >
          Back to API Keys
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/api-keys')}
            className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg dark:text-neutral-300"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{apiKey?.name}</h1>
            <p className="text-neutral-500 dark:text-neutral-400 flex items-center gap-2 mt-1">
              <Key className="h-4 w-4" />
              <code className="bg-neutral-100 dark:bg-neutral-700 dark:text-neutral-300 px-2 py-0.5 rounded">
                {apiKey?.keyPrefix}...
              </code>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditForm(apiKey);
                }}
                className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saveLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {saveLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
              >
                Edit
              </button>
              <button
                onClick={() => setShowRotateModal(true)}
                className="px-4 py-2 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded-lg flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Rotate
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-red-700 dark:text-red-300">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
            Dismiss
          </button>
        </div>
      )}

      {/* New Key Display */}
      {newKey && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-lg">
              <Key className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-green-900 dark:text-green-300">API Key Rotated!</h3>
              <p className="text-green-700 dark:text-green-400 text-sm mt-1">
                Copy this key now. You won't be able to see it again.
              </p>
              <div className="mt-4 flex items-center gap-2">
                <code className="flex-1 bg-white dark:bg-neutral-800 border border-green-300 dark:border-green-700 rounded px-3 py-2 font-mono text-sm break-all dark:text-green-300">
                  {newKey.key}
                </code>
                <button
                  onClick={handleCopyKey}
                  className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <button onClick={() => setNewKey(null)} className="text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Details Card */}
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Key Details</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Name</label>
            {isEditing ? (
              <input
                type="text"
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
              />
            ) : (
              <p className="text-neutral-900 dark:text-white">{apiKey?.name}</p>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Status</label>
            {isEditing ? (
              <select
                value={editForm.isActive ? 'active' : 'inactive'}
                onChange={(e) => setEditForm({ ...editForm, isActive: e.target.value === 'active' })}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            ) : (
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  apiKey?.isActive ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300' : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
                }`}
              >
                {apiKey?.isActive ? 'Active' : 'Inactive'}
              </span>
            )}
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Description</label>
            {isEditing ? (
              <textarea
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
              />
            ) : (
              <p className="text-neutral-900 dark:text-white">{apiKey?.description || 'No description'}</p>
            )}
          </div>

          {/* Permissions */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-2">Permissions</label>
            {isEditing ? (
              <div className="flex flex-wrap gap-2">
                {PERMISSIONS.map((perm) => (
                  <label
                    key={perm.value}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${
                      editForm.permissions?.includes(perm.value)
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                        : 'border-neutral-200 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
                    } dark:text-white`}
                  >
                    <input
                      type="checkbox"
                      checked={editForm.permissions?.includes(perm.value)}
                      onChange={() => togglePermission(perm.value)}
                      className="h-4 w-4 text-primary-600 rounded dark:bg-neutral-700 dark:border-neutral-600"
                    />
                    {perm.label}
                  </label>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {apiKey?.permissions?.map((perm) => (
                  <span
                    key={perm}
                    className="px-2 py-1 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded text-sm flex items-center gap-1"
                  >
                    <Shield className="h-3 w-3" />
                    {perm}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Rate Limits */}
          <div>
            <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">
              Rate Limit (per minute)
            </label>
            {isEditing ? (
              <input
                type="number"
                min="1"
                max="10000"
                value={editForm.rateLimitPerMinute || 60}
                onChange={(e) =>
                  setEditForm({ ...editForm, rateLimitPerMinute: parseInt(e.target.value) })
                }
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
              />
            ) : (
              <p className="text-neutral-900 dark:text-white">{apiKey?.rateLimitPerMinute} requests</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">
              Rate Limit (per hour)
            </label>
            {isEditing ? (
              <input
                type="number"
                min="1"
                max="100000"
                value={editForm.rateLimitPerHour || 1000}
                onChange={(e) =>
                  setEditForm({ ...editForm, rateLimitPerHour: parseInt(e.target.value) })
                }
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
              />
            ) : (
              <p className="text-neutral-900 dark:text-white">{apiKey?.rateLimitPerHour} requests</p>
            )}
          </div>

          {/* Usage Stats */}
          <div>
            <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Total Usage</label>
            <p className="text-neutral-900 dark:text-white flex items-center gap-2">
              <Activity className="h-4 w-4 text-neutral-400" />
              {apiKey?.usageCount?.toLocaleString()} requests
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Last Used</label>
            <p className="text-neutral-900 dark:text-white flex items-center gap-2">
              <Clock className="h-4 w-4 text-neutral-400" />
              {formatDate(apiKey?.lastUsedAt)}
            </p>
          </div>

          {/* Created Info */}
          <div>
            <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Created</label>
            <p className="text-neutral-900 dark:text-white">{formatDate(apiKey?.createdAt)}</p>
            {apiKey?.createdBy && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">by {apiKey.createdBy.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Expires</label>
            <p className="text-neutral-900 dark:text-white">
              {apiKey?.expiresAt ? formatDate(apiKey.expiresAt) : 'Never'}
            </p>
          </div>
        </div>
      </div>

      {/* Audit Log */}
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-neutral-200 dark:border-neutral-700">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Audit Log</h2>
          <p className="text-neutral-500 dark:text-neutral-400 text-sm">Recent API activity for this key</p>
        </div>

        {auditLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">
            No API activity recorded yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
              <thead className="bg-neutral-50 dark:bg-neutral-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">
                    Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">
                    Method
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">
                    Path
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">
                    Duration
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {auditLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                    <td className="px-4 py-3 text-sm text-neutral-500 dark:text-neutral-400 whitespace-nowrap">
                      {formatDate(log.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-900 dark:text-white">{log.action}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs font-medium rounded bg-neutral-100 dark:bg-neutral-700 dark:text-neutral-300">
                        {log.method}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-300 font-mono truncate max-w-xs">
                      {log.path}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(
                          log.responseStatus
                        )}`}
                      >
                        {log.responseStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-500 dark:text-neutral-400">
                      {log.responseTimeMs ? `${log.responseTimeMs}ms` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Delete API Key?</h2>
            <p className="text-neutral-600 dark:text-neutral-400 mt-2">
              This action cannot be undone. Any integrations using this key will stop working
              immediately.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rotate Modal */}
      {showRotateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Rotate API Key?</h2>
            <p className="text-neutral-600 dark:text-neutral-400 mt-2">
              This will generate a new key and invalidate the old one. You'll need to update any
              integrations using this key.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowRotateModal(false)}
                className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleRotate}
                disabled={rotateLoading}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {rotateLoading ? 'Rotating...' : 'Rotate Key'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
